<template>
  <div class="mx-auto px-4 py-4 w-[92%]">
    <!-- Шапка -->
    <div class="flex items-center justify-between mb-4">
      <div>
        <button type="button" @click="goBack" class="text-sm text-blue-600 hover:underline mb-1">← К реестру</button>
        <h1 class="text-lg font-semibold text-gray-800">ФР-УНП-02. Расследование. Версия 01</h1>
      </div>
      <span :class="statusClass(doc.status)" class="text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
        {{ statusLabel(doc.status) }}
      </span>
    </div>

    <div v-if="loading" class="text-sm text-gray-500">Загрузка...</div>

    <div v-else class="space-y-6">
      <!-- Раздел 1: Исходная информация -->
      <section class="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <h2 class="font-semibold text-gray-800">Исходная информация</h2>

        <div class="grid grid-cols-3 gap-4">
          <div>
            <label class="block text-sm text-gray-700 mb-1">Номер несоответствия</label>
            <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ doc.number || '—' }}</span>
            <input
              v-else
              v-model="doc.number"
              type="text"
              placeholder="РН-..."
              class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label class="block text-sm text-gray-700 mb-1">Дата</label>
            <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ investigationDateDisplay }}</span>
            <input v-else :value="investigationDateDisplay" type="text" disabled class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]" />
          </div>
          <div>
            <label class="block text-sm text-gray-700 mb-1">Ответственный</label>
            <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ creatorDisplay }}</span>
            <input v-else :value="creatorDisplay" type="text" disabled class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]" />
          </div>
          <div>
            <label class="block text-sm text-gray-700 mb-1">Источник документа</label>
            <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ originDisplay }}</span>
            <input v-else :value="originDisplay" type="text" disabled class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]" />
          </div>
        </div>

        <div>
          <label class="block text-sm text-gray-700 mb-1">Название несоответствия</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ doc.title || '—' }}</span>
          <input
            v-else
            v-model="doc.title"
            type="text"
            class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Описание несоответствия</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.description || '—' }}</span>
          <textarea
            v-else
            v-model="doc.description"
            rows="2"
            placeholder="Опишите, в чем именно заключается несоответствие, кем и где было выявлено"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>

        <!-- Затронутый продукт -->
        <div>
          <h3 class="text-sm font-semibold text-gray-800 mb-2">Затронутый продукт</h3>
          <table class="w-full text-sm border border-gray-200 table-fixed">
            <colgroup>
              <col style="width: 5%" /><col style="width: 47%" /><col style="width: 28%" /><col style="width: 14%" /><col style="width: 6%" />
            </colgroup>
            <thead class="bg-gray-50">
              <tr>
                <th class="px-2 py-1 text-center border-b">№</th>
                <th class="px-2 py-1 text-left border-b">Номер p/n</th>
                <th class="px-2 py-1 text-left border-b">Краткое название</th>
                <th class="px-2 py-1 text-left border-b">Количество</th>
                <th class="px-2 py-1 border-b"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="products.length === 0"><td colspan="5" class="px-2 py-2 text-gray-400 text-center">Нет строк</td></tr>
              <template v-for="(item, index) in products" :key="index">
              <tr class="border-t border-gray-200">
                <td class="px-2 py-1 align-top text-center text-gray-500">{{ index + 1 }}</td>
                <td class="px-2 py-1 align-top">
                  <span v-if="isReadOnly" class="text-sm text-gray-700">{{ item.product_name || '—' }}</span>
                  <div v-else class="flex items-start gap-1">
                    <div class="flex-1 min-w-0">
                      <div v-if="!item.manual" class="v-select-filter" style="width: 100%; flex: none;">
                        <v-select
                          v-model="item.selected"
                          :options="productResults"
                          :filterable="false"
                          :loading="productLoading"
                          :get-option-label="productLabel"
                          label="name"
                          placeholder="Выберите из номенклатуры"
                          :disabled="isReadOnly"
                          @search="(q) => onProductSearch(q, index)"
                          @open="() => onProductSearch('', index)"
                          @update:modelValue="(val) => onProductSelected(index, val)"
                        >
                          <template #option="o"><span>{{ productLabel(o) }}</span></template>
                          <template #no-options>
                            <span v-if="productLoading">Загрузка...</span>
                            <span v-else>Ничего не найдено</span>
                          </template>
                          <template #list-footer>
                            <li v-if="productHasMore" class="px-3 py-2 text-center border-t">
                              <button type="button" @click.prevent.stop="loadMoreProducts" :disabled="productLoading" class="text-blue-600 hover:text-blue-800 disabled:text-gray-400">
                                {{ productLoading ? 'Загрузка...' : 'Загрузить ещё...' }}
                              </button>
                            </li>
                          </template>
                        </v-select>
                      </div>
                      <input v-else v-model="item.product_name" :disabled="isReadOnly" type="text" placeholder="Ручной ввод" class="w-full border border-gray-300 rounded-lg px-3 text-sm disabled:bg-gray-100 h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <button v-if="!isReadOnly" type="button" @click="toggleManual(index)" :title="item.manual ? 'Выбрать из номенклатуры' : 'Ввести вручную'" class="flex-shrink-0 h-[39px] w-8 inline-flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50">
                      {{ item.manual ? '☰' : '✎' }}
                    </button>
                  </div>
                </td>
                <td class="px-2 py-1 align-top">
                  <span v-if="isReadOnly" class="text-sm text-gray-700">{{ item.short_name || '—' }}</span>
                  <input v-else v-model="item.short_name" type="text" class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </td>
                <td class="px-2 py-1 align-top">
                  <span v-if="isReadOnly" class="text-sm text-gray-700">{{ item.quantity ?? '—' }}</span>
                  <input v-else v-model="item.quantity" type="number" step="0.001" class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </td>
                <td class="px-2 py-1 align-top text-center">
                  <button v-if="!isReadOnly" type="button" @click="removeProduct(index)" class="h-[39px] text-red-600 hover:text-red-800" title="Удалить строку">✕</button>
                </td>
              </tr>
              <!-- Привязка строки к конкретному изделию (ТЗ-7.3): необязательна -->
              <tr class="bg-gray-50">
                <td class="px-2 pb-2 text-gray-400"></td>
                <td class="px-2 pb-2" colspan="4">
                  <div class="text-xs font-semibold text-gray-600 mb-1">Привязка к изделию</div>
                  <ProductBindingSelector
                    v-model="item.binding"
                    :read-only="isReadOnly"
                    @product-picked="(picked) => onBindingProductPicked(index, picked)"
                  />
                </td>
              </tr>
              </template>
            </tbody>
          </table>
          <div v-if="!isReadOnly" class="mt-2">
            <button type="button" @click="addProduct" class="text-sm text-blue-600 hover:underline">+ Добавить строку</button>
          </div>
        </div>

        <!-- Перемещение в карантин -->
        <div>
          <label class="block text-sm text-gray-700 mb-1">Перемещение в карантин</label>
          <div class="flex gap-4 mb-2">
            <label class="inline-flex items-center gap-1 text-sm"><input type="radio" :value="true" v-model="doc.quarantine" :disabled="isReadOnly" /> Да</label>
            <label class="inline-flex items-center gap-1 text-sm"><input type="radio" :value="false" v-model="doc.quarantine" :disabled="isReadOnly" /> Нет</label>
          </div>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.quarantine_note || '—' }}</span>
          <textarea
            v-else
            v-model="doc.quarantine_note"
            rows="2"
            :placeholder="quarantineNoteLabel"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>

        <div>
          <label class="block text-sm text-gray-700 mb-1">Немедленные действия</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.immediate_actions || '—' }}</span>
          <textarea
            v-else
            v-model="doc.immediate_actions"
            rows="2"
            placeholder="Опишите какие были предприняты немедленные действия, если такие были"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>
      </section>

      <!-- Раздел 2: Расследование -->
      <section class="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <h2 class="font-semibold text-gray-800">Расследование</h2>

        <div>
          <label class="block text-sm text-gray-700 mb-1">Ответственный за расследование</label>
          <div v-if="!isReadOnly" class="v-select-filter" style="width: 100%; flex: none;">
            <v-select v-model="doc.investigator_user_id" :options="users" :reduce="u => u.id" :get-option-label="userName" placeholder="Выберите пользователя" />
          </div>
          <div v-else class="text-sm">{{ investigatorName() }}</div>
        </div>

        <!-- Методические указания по проведению расследования -->
        <div class="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2 leading-relaxed">
          <p>Расследование выполняет ответственный за расследование. (указывается вручную)</p>
          <p>Проведите расследования появления несоответствия. В ходе расследования используйте один или несколько из методов:</p>
          <ul class="list-disc pl-5 space-y-0.5">
            <li>анализ данных;</li>
            <li>5 почему;</li>
            <li>диаграмма Исикавы;</li>
            <li>повторение процесса;</li>
            <li>опрос сотрудников;</li>
            <li>какой – либо другой метод.</li>
          </ul>
          <p>В ходе расследования старайтесь опираться на факты, указывайте ссылки на документы. Допускается добавлять фотографии, создавать таблицы или графики.</p>
          <p>Основной целью является поиск первопричины по которой возникло несоответствие. В конце расследование подведите итоги и напишите вывод.</p>
        </div>

        <div>
          <label class="block text-sm font-semibold text-gray-800 mb-1">Виды первопричин</label>
          <div class="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
            <p class="text-sm text-gray-600 leading-relaxed sm:w-[12.5%]">На основании расследовании укажите или выделите ОДНУ из строк, которая больше подходит к первопричине несоответствия</p>
            <div class="space-y-1">
              <label v-for="opt in rootCauses" :key="opt.value" class="flex items-center gap-2 text-sm">
                <input type="radio" :value="opt.value" v-model="doc.root_cause" :disabled="isReadOnly" />
                <span>{{ opt.value }}. {{ opt.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <div>
          <label class="block text-sm text-gray-700 mb-1">Требуется ли коррекция продукта</label>
          <div class="flex gap-4 mb-2">
            <label class="inline-flex items-center gap-1 text-sm"><input type="radio" :value="true" v-model="doc.correction_required" :disabled="isReadOnly" /> Да</label>
            <label class="inline-flex items-center gap-1 text-sm"><input type="radio" :value="false" v-model="doc.correction_required" :disabled="isReadOnly" /> Нет</label>
          </div>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.correction_note || '—' }}</span>
          <textarea
            v-else
            v-model="doc.correction_note"
            rows="2"
            :placeholder="correctionNoteLabel"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>

        <div>
          <label class="block text-sm font-semibold text-gray-800 mb-1">Действие с продуктом</label>
          <div class="space-y-1">
            <label v-for="opt in productActions" :key="opt.value" class="flex items-center gap-2 text-sm">
              <input type="radio" :value="opt.value" v-model="doc.product_action" :disabled="isReadOnly" />
              <span>{{ opt.value }}. {{ opt.label }}</span>
            </label>
          </div>
        </div>

        <div>
          <label class="block text-sm text-gray-700 mb-1">Оценка рисков</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.risk_note || '—' }}</span>
          <textarea
            v-else
            v-model="doc.risk_note"
            rows="2"
            placeholder="Укажите все риски, которые могут возникнуть для продукции, процесса и т.п. при выборе того или иного действия"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>
      </section>

      <!-- Раздел 3: Корректирующие и предупреждающие действия -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <h2 class="font-semibold text-gray-800 mb-3">Корректирующие и предупреждающие действия</h2>
        <table class="w-full text-sm border border-gray-200 table-fixed">
          <colgroup>
            <col style="width: 4%" /><col style="width: 31%" /><col style="width: 20%" /><col style="width: 14%" /><col style="width: 14%" /><col style="width: 13%" /><col style="width: 4%" />
          </colgroup>
          <thead class="bg-gray-50">
            <tr>
              <th class="px-2 py-1 text-center border-b">№</th>
              <th class="px-2 py-1 text-left border-b">Описание</th>
              <th class="px-2 py-1 text-left border-b">Ответственный</th>
              <th class="px-2 py-1 text-left border-b">Плановая дата</th>
              <th class="px-2 py-1 text-left border-b">Дата выполнения</th>
              <th class="px-2 py-1 text-left border-b">Комментарии</th>
              <th class="px-2 py-1 border-b"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="actions.length === 0"><td colspan="7" class="px-2 py-2 text-gray-400 text-center">Нет строк</td></tr>
            <tr v-for="(act, index) in actions" :key="index" class="border-t border-gray-200">
              <td class="px-2 py-1 align-top text-center text-gray-500">{{ index + 1 }}</td>
              <td class="px-2 py-1 align-top">
                <span v-if="isReadOnly" class="text-sm text-gray-700 whitespace-pre-line">{{ act.description || '—' }}</span>
                <textarea v-else v-model="act.description" rows="2" class="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-500"></textarea>
              </td>
              <td class="px-2 py-1 align-top">
                <div v-if="!isReadOnly" class="v-select-filter" style="width: 100%; flex: none;">
                  <v-select v-model="act.responsible_user_id" :options="users" :reduce="u => u.id" :get-option-label="userName" placeholder="Выберите" />
                </div>
                <span v-else>{{ responsibleName(act) }}</span>
              </td>
              <td class="px-2 py-1 align-top">
                <span v-if="isReadOnly" class="text-sm text-gray-700">{{ formatDate(act.planned_date) }}</span>
                <input v-else v-model="act.planned_date" type="date" class="w-full border border-gray-300 rounded-lg px-2 text-sm h-[39px] focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </td>
              <td class="px-2 py-1 align-top">
                <span v-if="isReadOnly" class="text-sm text-gray-700">{{ formatDate(act.done_date) }}</span>
                <input v-else v-model="act.done_date" type="date" class="w-full border border-gray-300 rounded-lg px-2 text-sm h-[39px] focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </td>
              <td class="px-2 py-1 align-top">
                <span v-if="isReadOnly" class="text-sm text-gray-700 whitespace-pre-line">{{ act.comment || '—' }}</span>
                <textarea v-else v-model="act.comment" rows="2" class="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-500"></textarea>
              </td>
              <td class="px-2 py-1 align-top text-center">
                <button v-if="!isReadOnly" type="button" @click="removeAction(index)" class="text-red-600 hover:text-red-800" title="Удалить строку">✕</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!isReadOnly" class="mt-2">
          <button type="button" @click="addAction" class="text-sm text-blue-600 hover:underline">+ Добавить строку</button>
        </div>
      </section>

      <!-- Раздел 4: Согласование -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <h2 class="font-semibold text-gray-800 mb-3">Согласование</h2>
        <table class="w-full text-sm border border-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-2 py-1 text-left border-b">ФИО</th>
              <th class="px-2 py-1 text-left border-b w-1/3">Позиция</th>
              <th class="px-2 py-1 text-left border-b w-44">Дата одобрения</th>
              <th class="px-2 py-1 border-b w-10"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="approvers.length === 0"><td colspan="4" class="px-2 py-2 text-gray-400 text-center">Нет согласующих</td></tr>
            <tr v-for="(approver, index) in approvers" :key="index" class="border-t border-gray-200">
              <td class="px-2 py-1 align-middle">
                <div v-if="!isReadOnly" class="v-select-filter" style="width: 100%; flex: none;">
                  <v-select v-model="approver.user_id" :options="users" :reduce="u => u.id" :get-option-label="userName" placeholder="Выберите пользователя" />
                </div>
                <span v-else>{{ approverName(approver) }}</span>
              </td>
              <td class="px-2 py-1 align-middle">
                <input v-if="!isReadOnly" v-model="approver.position_text" type="text" class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                <span v-else>{{ approver.position_text || '—' }}</span>
              </td>
              <td class="px-2 py-1 align-middle">
                <span v-if="approver.approved_at" class="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Одобрено {{ formatDate(approver.approved_at) }}</span>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td class="px-2 py-1 align-middle text-center">
                <button v-if="!isReadOnly" type="button" @click="removeApprover(index)" class="text-red-600 hover:text-red-800" title="Удалить строку">✕</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!isReadOnly" class="mt-3">
          <button type="button" @click="addApprover" class="text-sm text-blue-600 hover:underline">+ Добавить согласующего</button>
        </div>
      </section>

      <!-- Кнопки действий -->
      <div class="flex flex-wrap gap-2 justify-end">
        <button v-if="canSaveDraft" v-can-write type="button" @click="saveDraft" :disabled="saving" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50">
          {{ saving ? 'Сохранение...' : (doc.status === 'draft' ? 'Сохранить как черновик' : 'Сохранить') }}
        </button>
        <button v-if="canSendForApproval" v-can-write type="button" @click="sendForApproval" :disabled="saving" class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-medium disabled:opacity-50">
          Отправить на согласование
        </button>
        <button v-if="canApprove" v-can-write type="button" @click="approveDoc" :disabled="saving" class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium disabled:opacity-50">
          Одобрить
        </button>
        <button v-if="canCancel" v-can-write type="button" @click="cancelDoc" :disabled="saving" class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium disabled:opacity-50">
          Отменить
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useAuthStore } from '@/stores/auth.js';
import ProductBindingSelector from '@/components/smkDesk/ProductBindingSelector.vue';
import { emptyBinding, bindingFromRow, bindingToPayload } from '@/components/smkDesk/productBinding.js';

// Виды первопричин (1–7) и действия с продуктом (1–4) — из формы РН.
const ROOT_CAUSES = [
  { value: 1, label: 'Проблема с внутренним процессом' },
  { value: 2, label: 'Несоответствие внутренним или внешним документам' },
  { value: 3, label: 'Проблема с материалом' },
  { value: 4, label: 'Проблема с оборудованием' },
  { value: 5, label: 'Ошибка сотрудника Компании' },
  { value: 6, label: 'Ошибка поставщика' },
  { value: 7, label: 'Первопричина не определена' },
];
const PRODUCT_ACTIONS = [
  { value: 1, label: 'Использование продукта (нет проблемы)' },
  { value: 2, label: 'Использовать после коррекции продукта (доработка или переделка)' },
  { value: 3, label: 'Оставить как есть' },
  { value: 4, label: 'Брак продукта' },
];

export default {
  components: { ProductBindingSelector },
  props: {
    moduleId: { type: [String, Number], required: true },
    ncId: { type: [String, Number], default: null },
    mode: { type: String, default: 'create' },
  },
  data() {
    return {
      docId: this.ncId ? Number(this.ncId) : null,
      localMode: this.mode,
      loading: false,
      saving: false,
      currentUser: null,
      creator: null,
      investigator: null,
      users: [],
      doc: this.emptyDoc(),
      products: [],
      actions: [],
      approvers: [],
      productResults: [],
      productLoading: false,
      productHasMore: false,
      productPage: 1,
      productQuery: '',
      rootCauses: ROOT_CAUSES,
      productActions: PRODUCT_ACTIONS,
    };
  },
  computed: {
    isReadOnly() {
      return ['approved', 'cancelled'].includes(this.doc.status);
    },
    investigationDateDisplay() {
      return this.doc.investigation_date ? this.formatDate(this.doc.investigation_date) : this.formatDate(new Date());
    },
    creatorDisplay() {
      return this.userName(this.creator);
    },
    // Из какого события создан документ — подпись приходит с бэкенда, ключ на экран не выводим
    originDisplay() {
      return this.doc.origin_label || 'Заведён вручную';
    },
    isCreator() {
      return this.currentUser && this.creator && this.currentUser.id === this.creator.id;
    },
    myApprover() {
      if (!this.currentUser) return null;
      return this.approvers.find(a => a.user_id === this.currentUser.id) || null;
    },
    canSaveDraft() {
      return !this.isReadOnly;
    },
    canSendForApproval() {
      return this.docId && this.doc.status === 'draft' && this.isCreator;
    },
    canApprove() {
      return this.doc.status === 'in_approval' && this.myApprover && !this.myApprover.approved_at;
    },
    canCancel() {
      return this.docId && this.doc.status !== 'cancelled' && this.isCreator;
    },
    quarantineNoteLabel() {
      if (this.doc.quarantine === true) return 'Укажите, в какой карантин перемещена продукция или материалы';
      if (this.doc.quarantine === false) return 'Укажите, почему принято решение не переводить в карантин';
      return 'Комментарий';
    },
    correctionNoteLabel() {
      if (this.doc.correction_required === true) return 'Укажите, какая коррекция продукта требуется, опишите действия. Если необходима дополнительная инструкция, то ее следует разработать до начала коррекции. К коррекции относится передела и доработка продукта.';
      if (this.doc.correction_required === false) return 'Укажите, почему не требуется коррекция';
      return 'Комментарий';
    },
  },
  mounted() {
    this.currentUser = useAuthStore().currentUser;
    this.loadUsers();
    if (this.localMode === 'edit' && this.docId) {
      this.loadDocument();
    } else {
      this.initCreate();
    }
  },
  methods: {
    emptyDoc() {
      return {
        number: 'РН-',
        investigation_date: null,
        title: '',
        description: '',
        quarantine: null,
        quarantine_note: '',
        immediate_actions: '',
        investigator_user_id: null,
        root_cause: null,
        correction_required: null,
        correction_note: '',
        product_action: null,
        risk_note: '',
        status: 'draft',
        origin_label: '',
      };
    },
    emptyProduct() {
      return { id: null, product_id: null, product_name: '', short_name: '', quantity: null, selected: null, manual: false, binding: emptyBinding() };
    },
    emptyAction() {
      return { id: null, description: '', responsible_user_id: null, planned_date: '', done_date: '', comment: '' };
    },
    initCreate() {
      this.creator = this.currentUser;
      this.doc = this.emptyDoc();
      this.products = [];
      this.actions = [];
      this.approvers = [];
      this.ensureFirstRows();
    },
    ensureFirstRows() {
      if (this.isReadOnly) return;
      if (this.products.length === 0) this.products.push(this.emptyProduct());
      if (this.actions.length === 0) this.actions.push(this.emptyAction());
      if (this.approvers.length === 0) this.addApprover();
    },
    async loadUsers() {
      try {
        const { data } = await axios.get('/api/smk/nc-investigation/users');
        if (data?.success) this.users = data.data || [];
      } catch (e) {
        console.error('Не удалось загрузить список пользователей', e);
      }
    },
    async loadDocument() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/smk/nc-investigation/${this.docId}`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.ensureFirstRows();
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка загрузки документа');
      } finally {
        this.loading = false;
      }
    },
    applyDocument(d) {
      this.docId = d.id;
      this.localMode = 'edit';
      this.creator = d.creator || null;
      this.investigator = d.investigator || null;
      this.doc = {
        number: d.number || '',
        investigation_date: d.investigation_date || null,
        title: d.title || '',
        description: d.description || '',
        quarantine: d.quarantine,
        quarantine_note: d.quarantine_note || '',
        immediate_actions: d.immediate_actions || '',
        investigator_user_id: d.investigator_user_id ?? null,
        root_cause: d.root_cause ?? null,
        correction_required: d.correction_required,
        correction_note: d.correction_note || '',
        product_action: d.product_action ?? null,
        risk_note: d.risk_note || '',
        status: d.status,
        origin_label: d.origin_label || '',
      };
      this.products = (d.products || []).map(p => ({
        id: p.id,
        product_id: p.product_id,
        product_name: p.product_name || '',
        short_name: p.short_name || '',
        quantity: p.quantity == null ? null : Number(p.quantity),
        selected: p.product_id ? { id: p.product_id, name: p.product_name } : null,
        manual: !!(p.product_name && !p.product_id),
        binding: bindingFromRow(p),
      }));
      this.actions = (d.actions || []).map(a => ({
        id: a.id,
        description: a.description || '',
        responsible_user_id: a.responsible_user_id,
        responsible: a.responsible || null,
        planned_date: this.toDateInput(a.planned_date),
        done_date: this.toDateInput(a.done_date),
        comment: a.comment || '',
      }));
      this.approvers = (d.approvers || []).map(a => ({
        id: a.id,
        user_id: a.user_id,
        position_text: a.position_text || '',
        approved_at: a.approved_at || null,
      }));
    },
    buildPayload() {
      return {
        number: this.doc.number || null,
        title: this.doc.title || null,
        description: this.doc.description || null,
        quarantine: this.doc.quarantine,
        quarantine_note: this.doc.quarantine_note || null,
        immediate_actions: this.doc.immediate_actions || null,
        investigator_user_id: this.doc.investigator_user_id ?? null,
        root_cause: this.doc.root_cause ?? null,
        correction_required: this.doc.correction_required,
        correction_note: this.doc.correction_note || null,
        product_action: this.doc.product_action ?? null,
        risk_note: this.doc.risk_note || null,
        products: this.products.map((p, idx) => ({
          id: p.id || undefined,
          product_id: p.product_id || null,
          product_name: p.product_name || null,
          short_name: p.short_name || null,
          quantity: p.quantity === '' || p.quantity == null ? null : p.quantity,
          sort: idx,
          ...bindingToPayload(p.binding),
        })),
        actions: this.actions.map((a, idx) => ({
          id: a.id || undefined,
          description: a.description || null,
          responsible_user_id: a.responsible_user_id || null,
          planned_date: a.planned_date || null,
          done_date: a.done_date || null,
          comment: a.comment || null,
          sort: idx,
        })),
        approvers: this.approvers.filter(a => a.user_id).map((a, idx) => ({
          id: a.id || undefined,
          user_id: a.user_id,
          position_text: a.position_text || null,
          sort: idx,
        })),
      };
    },
    async persist() {
      const payload = this.buildPayload();
      if (!this.docId) {
        const { data } = await axios.post('/api/smk/nc-investigation', payload);
        if (data?.success) {
          this.applyDocument(data.data);
          this.$router.replace({ path: `/smk-desk/${this.moduleId}/nc-investigation/${this.docId}`, query: this.$route.query });
          return true;
        }
        return false;
      }
      const { data } = await axios.put(`/api/smk/nc-investigation/${this.docId}`, payload);
      if (data?.success) {
        this.applyDocument(data.data);
        return true;
      }
      return false;
    },
    async saveDraft() {
      this.saving = true;
      try {
        if (await this.persist()) this.notifySuccess('Документ сохранён');
      } catch (e) {
        this.notifyError(e, 'Ошибка сохранения');
      } finally {
        this.saving = false;
      }
    },
    async sendForApproval() {
      this.saving = true;
      try {
        if (!(await this.persist())) return;
        const { data } = await axios.post(`/api/smk/nc-investigation/${this.docId}/send-for-approval`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ отправлен на согласование');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка отправки на согласование');
      } finally {
        this.saving = false;
      }
    },
    async approveDoc() {
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/smk/nc-investigation/${this.docId}/approve`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ одобрен');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка одобрения');
      } finally {
        this.saving = false;
      }
    },
    async cancelDoc() {
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/smk/nc-investigation/${this.docId}/cancel`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ отменён');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка отмены');
      } finally {
        this.saving = false;
      }
    },
    addProduct() { this.products.push(this.emptyProduct()); },
    removeProduct(index) { this.products.splice(index, 1); },
    // Изделие выбрано — подставляем номенклатуру в пустые поля строки, заполненное не трогаем
    onBindingProductPicked(index, picked) {
      const item = this.products[index];
      if (!item || !picked) return;
      if (picked.product_id && !item.product_id && !item.manual) {
        item.product_id = picked.product_id;
        item.product_name = picked.product_name || item.product_name;
        item.selected = { id: picked.product_id, name: picked.product_name };
      }
      if (picked.product_name && !item.short_name) item.short_name = picked.product_name;
    },
    toggleManual(index) {
      const item = this.products[index];
      item.manual = !item.manual;
      item.selected = null;
      item.product_id = null;
      if (!item.manual) item.product_name = '';
    },
    addAction() { this.actions.push(this.emptyAction()); },
    removeAction(index) { this.actions.splice(index, 1); },
    addApprover() { this.approvers.push({ id: null, user_id: null, position_text: '', approved_at: null }); },
    removeApprover(index) { this.approvers.splice(index, 1); },
    productLabel(option) {
      if (!option) return '';
      return option.sku ? `${option.sku} — ${option.name}` : option.name;
    },
    onProductSearch(query, index) {
      this.productQuery = query || '';
      this.productPage = 1;
      clearTimeout(this._productTimer);
      this._productTimer = setTimeout(() => this.fetchProducts(false), 300);
    },
    async fetchProducts(append) {
      this.productLoading = true;
      try {
        const { data } = await axios.post('/api/product/getProducts', { search: this.productQuery.trim(), page: this.productPage, per_page: 20 });
        const paginator = data?.data || {};
        const rows = paginator.data || [];
        this.productResults = append ? [...this.productResults, ...rows] : rows;
        const current = paginator.current_page || this.productPage;
        const last = paginator.last_page || current;
        this.productHasMore = current < last;
      } catch (e) {
        if (!append) this.productResults = [];
        this.productHasMore = false;
      } finally {
        this.productLoading = false;
      }
    },
    loadMoreProducts() {
      if (this.productLoading || !this.productHasMore) return;
      this.productPage += 1;
      this.fetchProducts(true);
    },
    onProductSelected(index, val) {
      this.products[index].selected = val || null;
      this.products[index].product_id = val && val.id ? val.id : null;
      this.products[index].product_name = val ? val.name : '';
      if (val && val.name && !this.products[index].short_name) this.products[index].short_name = val.name;
    },
    goBack() {
      this.$router.push({ path: `/smk-desk/${this.moduleId}`, query: { ...this.$route.query, tab: 'nc' } });
    },
    userName(user) {
      if (!user) return '—';
      const full = [user.last_name, user.first_name, user.patronymic].filter(Boolean).join(' ');
      return full || user.name || user.email || ('ID ' + user.id);
    },
    approverName(approver) {
      if (approver.user) return this.userName(approver.user);
      const u = this.users.find(x => x.id === approver.user_id);
      if (u) return this.userName(u);
      if (this.creator && this.creator.id === approver.user_id) return this.userName(this.creator);
      return approver.user_id ? ('ID ' + approver.user_id) : '—';
    },
    responsibleName(act) {
      if (act.responsible) return this.userName(act.responsible);
      const u = this.users.find(x => x.id === act.responsible_user_id);
      return u ? this.userName(u) : (act.responsible_user_id ? ('ID ' + act.responsible_user_id) : '—');
    },
    investigatorName() {
      if (this.investigator) return this.userName(this.investigator);
      const u = this.users.find(x => x.id === this.doc.investigator_user_id);
      return u ? this.userName(u) : (this.doc.investigator_user_id ? ('ID ' + this.doc.investigator_user_id) : '—');
    },
    statusLabel(status) {
      return { draft: 'Новый', in_approval: 'В работе', approved: 'Согласован', cancelled: 'Отменён' }[status] || status;
    },
    statusClass(status) {
      return {
        draft: 'bg-gray-100 text-gray-700',
        in_approval: 'bg-yellow-100 text-yellow-700',
        approved: 'bg-green-100 text-green-700',
        cancelled: 'bg-red-100 text-red-700',
      }[status] || 'bg-gray-100 text-gray-700';
    },
    formatDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '—';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    },
    toDateInput(value) {
      if (!value) return '';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    notifySuccess(message) {
      if (this.$root?.$toast?.success) this.$root.$toast.success(message);
    },
    notifyError(error, fallback) {
      const msg = error?.response?.data?.message || fallback || 'Ошибка';
      if (this.$root?.$toast?.error) this.$root.$toast.error(msg);
      else alert(msg);
    },
  },
};
</script>
